import { useAbility } from "config/abilityContext";
import { formatMoney } from "helpers/numberHelper";
import React from "react";
import { useTranslation } from "react-i18next";
import { formatToLocaleDate } from "utils/dateUtils";
import { fallback } from "utils/stringUtils";



interface Props { teacher: any; }


const InitialsAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name.split(" ").filter(Boolean).map(p => p[0]?.toUpperCase()).slice(0, 2).join("") || "—";
  return (
    <div className="d-flex align-items-center justify-content-center avatar avatar-xxl bg-light text-dark border">
      <span className="fs-3">{initials}</span>
    </div>
  );
};

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <div className="row mb-2">
      <div className="col-md-3 col-6 fw-medium text-dark">{label}</div>
      <div className="col-md-9 col-6">{value}</div>
    </div>
  ) : null;

const TeacherDetailsCards: React.FC<Props> = ({ teacher }) => {
  const { t } = useTranslation();
  const ability = useAbility();
  if (!teacher) {
    return null;
  }
  const user = teacher?.user;
  const staff = teacher?.staff || undefined;

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() || t("unknown", "Unknown");

  const mainSubject =
    teacher?.assignments?.[0]?.subjectName ||
    teacher?.assignments?.find((a: any) => !!a.subjectName)?.subjectName;

  return (
    <>
      {ability.can("getAll", "Teacher") && (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {user?.photo ? (
                  <img src={user.photo} alt="avatar" className="avatar avatar-xxl border" />
                ) : (
                  <InitialsAvatar name={fullName} />
                )}
                <div className="flex-grow-1">
                  <h4 className="mb-1 text-truncate">{fullName}</h4>
                  {teacher.status && (
                    <div className="text-primary mb-1">
                      <i className="ti ti-badge" /> {t("status")}:{" "}
                      <span className="text-lowercase">{teacher.status}</span>
                    </div>
                  )}
                  {teacher.createdAt && (
                    <div className="text-muted mb-1">
                      <i className="ti ti-calendar" /> {t("joined_on", "Joined")} :{" "}
                      {formatToLocaleDate(teacher.createdAt)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(user?.gender || user?.birthDate || user?.cityOfBirth || user?.city || user?.address || user?.zipCode) && (
            <div className="card mb-4">
              <div className="card-header"><h5 className="mb-0"> <i className="ti ti-id" /> {t("basic_information")}</h5></div>
              <div className="card-body">
                <Row label={t("gender")} value={user?.gender && t(user.gender)} />
                <Row label={t("birth_date")} value={user?.birthDate && formatToLocaleDate(user.birthDate)} />
                <Row label={t("city_of_birth")} value={user?.cityOfBirth && fallback(user.cityOfBirth)} />
                <Row label={t("city")} value={user?.city && fallback(user.city)} />
                <Row label={t("address")} value={user?.address && fallback(user.address)} />
                <Row label={t("zip_code")} value={user?.zipCode && fallback(user.zipCode)} />
              </div>
            </div>
          )}

          {(user?.phoneNumber || user?.email) && (
            <div className="card mb-4">
              <div className="card-header"><h5 className="mb-0">  <i className="ti ti-address-book" /> {t("primary_contact_info")}</h5></div>
              <div className="card-body">
                <Row label={t("phone_number")} value={user?.phoneNumber} />
                <Row label={t("email_address")} value={user?.email} />
              </div>
            </div>
          )}
        </>
      )}

      {ability.can("getAll", "Staff") && (
        <>
          {staff && (staff.contract || staff.monthlySalary || staff.startDate || staff.endDate) && (
            <div className="card mb-4">
              <div className="card-header"><h5 className="mb-0"> <i className="ti ti-users-group" /> {t("staff.info_contract")}</h5></div>
              <div className="card-body">
                <Row label={t("staff.form.contractType")} value={staff.contract && fallback(staff.contract)} />
                <Row label={t("staff.form.monthlySalary")} value={staff.monthlySalary && formatMoney(staff.monthlySalary)} />
                <Row label={t("staff.form.startDate")} value={staff.startDate && formatToLocaleDate(staff.startDate)} />
                <Row label={t("staff.form.endDate")} value={staff.endDate && formatToLocaleDate(staff.endDate)} />
              </div>
            </div>
          )}
        </>
      )}

      {/* {teacher?.assignments && teacher?.assignments?.length > 0 && (
        <div className="card mb-4">
          <div className="card-header"><h5 className="mb-0">{t("assignments")}</h5></div>
          <div className="card-body">
            <ul className="mb-0">
              {teacher.assignments.map((a: any) => (
                <li key={a.subjectId} className="mb-1">
                  <strong>{a.subjectName}</strong>{" "}
                  {a.classIds?.length ? (
                    <span className="text-muted">
                      — {a.classIds.length} {a.classIds.length > 1 ? t("classes") : t("class")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )} */}
    </>
  );
};

export default TeacherDetailsCards;
